import { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import ImportarRazaoService, {
  AnaliseLayout,
  GrupoFormato,
  RazaoLayout,
  ValidacaoLayout,
} from "../../../services/ImportarRazaoService";

interface Props {
  uploadId: number;
  empresaNome?: string;
  onConcluido: () => void;
  onClose: () => void;
}

type Papel =
  | "" | "CONTA" | "CONTA_NOME" | "DATA" | "HISTORICO" | "DEBITO" | "CREDITO"
  | "VALOR" | "DC" | "SALDO" | "LOTE" | "CONTRAPARTIDA";

const PAPEIS: { valor: Papel; rotulo: string }[] = [
  { valor: "", rotulo: "Ignorar" },
  { valor: "CONTA", rotulo: "Conta" },
  { valor: "CONTA_NOME", rotulo: "Nome da conta" },
  { valor: "DATA", rotulo: "Data" },
  { valor: "HISTORICO", rotulo: "Histórico" },
  { valor: "DEBITO", rotulo: "Débito" },
  { valor: "CREDITO", rotulo: "Crédito" },
  { valor: "VALOR", rotulo: "Valor (coluna única)" },
  { valor: "DC", rotulo: "Indicador D/C" },
  { valor: "SALDO", rotulo: "Saldo" },
  { valor: "LOTE", rotulo: "Lote/Documento" },
  { valor: "CONTRAPARTIDA", rotulo: "Contrapartida" },
];

const LINHAS_EXIBIDAS = 40;
const letra = (i: number) => {
  let s = "";
  for (let n = i + 1; n > 0; n = Math.floor((n - 1) / 26)) s = String.fromCharCode(65 + ((n - 1) % 26)) + s;
  return s;
};
const brl = (v: number) => v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Coluna (1-based) de cada papel, a partir do layout. */
function papeisDoLayout(l: RazaoLayout, colunas: number): Papel[] {
  const p: Papel[] = Array(colunas).fill("");
  const set = (col: number | null | undefined, papel: Papel) => {
    if (col && col >= 1 && col <= colunas) p[col - 1] = papel;
  };
  set(l.modoConta === "BLOCO" ? l.contaColuna : l.contaCodigoColuna, "CONTA");
  if (l.modoConta === "COLUNA") set(l.contaNomeColuna, "CONTA_NOME");
  set(l.historicoColuna, "HISTORICO");
  set(l.debitoColuna, "DEBITO");
  set(l.creditoColuna, "CREDITO");
  set(l.valorColuna, "VALOR");
  set(l.indicadorDCColuna, "DC");
  set(l.saldoColuna, "SALDO");
  set(l.loteColuna, "LOTE");
  set(l.contrapartidaColuna, "CONTRAPARTIDA");
  set(l.dataColuna, "DATA");
  return p;
}

function layoutDosPapeis(papeis: Papel[], base: RazaoLayout): RazaoLayout {
  const col = (papel: Papel) => {
    const i = papeis.indexOf(papel);
    return i >= 0 ? i + 1 : null;
  };
  const bloco = base.modoConta === "BLOCO";
  return {
    ...base,
    dataColuna: col("DATA") ?? 0,
    historicoColuna: col("HISTORICO"),
    debitoColuna: col("DEBITO"),
    creditoColuna: col("CREDITO"),
    valorColuna: col("VALOR"),
    indicadorDCColuna: col("DC"),
    saldoColuna: col("SALDO"),
    loteColuna: col("LOTE"),
    contrapartidaColuna: col("CONTRAPARTIDA"),
    contaColuna: bloco ? col("CONTA") : null,
    contaCodigoColuna: bloco ? null : col("CONTA"),
    contaNomeColuna: bloco ? null : col("CONTA_NOME"),
    linhasIgnorarComecandoCom: base.linhasIgnorarComecandoCom ?? [],
  };
}

const LAYOUT_VAZIO: RazaoLayout = { modoConta: "COLUNA", linhaCabecalho: 1, dataColuna: 0, linhasIgnorarComecandoCom: [] };

/**
 * DEVN3-602: tela de mapeamento de colunas — aberta quando a IA não conseguiu um layout que feche a
 * conferência (ou o arquivo não tem cabeçalho e débito/crédito precisam de confirmação). O usuário NÃO
 * edita o arquivo: só diz o que cada coluna é, sobre uma amostra somente leitura. Validar confere os
 * saldos da amostra no servidor, sem IA e sem custo.
 */
const ModalMapeamentoColunas = ({ uploadId, empresaNome, onConcluido, onClose }: Props) => {
  const [analise, setAnalise] = useState<AnaliseLayout | null>(null);
  const [grupoId, setGrupoId] = useState<string>("");
  const [base, setBase] = useState<RazaoLayout>(LAYOUT_VAZIO);
  const [papeis, setPapeis] = useState<Papel[]>([]);
  const [resultado, setResultado] = useState<ValidacaoLayout | null>(null);
  const [confirmouDC, setConfirmouDC] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const carregar = async () => {
    const a = await ImportarRazaoService.analise(uploadId);
    setAnalise(a);
    const pendente = a.grupos.find((g) => g.status !== "IDENTIFICADO");
    if (pendente) selecionarGrupo(pendente);
  };

  useEffect(() => {
    carregar().catch(() => Swal.fire({ icon: "error", text: "Não foi possível carregar o mapeamento." }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadId]);

  function selecionarGrupo(g: GrupoFormato) {
    const l = { ...LAYOUT_VAZIO, ...(g.layoutSugerido ?? {}) } as RazaoLayout;
    setGrupoId(g.id);
    setBase(l);
    setPapeis(papeisDoLayout(l, g.colunas));
    setResultado(null);
    setConfirmouDC(false);
  }

  const grupo = useMemo(() => analise?.grupos.find((g) => g.id === grupoId), [analise, grupoId]);
  const pendentes = useMemo(() => (analise?.grupos ?? []).filter((g) => g.status !== "IDENTIFICADO"), [analise]);
  const layoutAtual = useMemo(() => layoutDosPapeis(papeis, base), [papeis, base]);

  function mudarPapel(coluna: number, papel: Papel) {
    setPapeis((prev) => prev.map((p, i) => (i === coluna ? papel : p === papel && papel !== "" ? "" : p)));
    setResultado(null);
  }

  function mudarBase(patch: Partial<RazaoLayout>) {
    setBase((b) => ({ ...b, ...patch }));
    setResultado(null);
  }

  async function validar(salvar: boolean) {
    if (!grupo) return;
    setEnviando(true);
    try {
      const r = await ImportarRazaoService.validarLayoutManual(uploadId, {
        grupoId: grupo.id,
        layout: layoutAtual,
        salvar,
        confirmouDebitoCredito: confirmouDC,
      });
      setResultado(r);
      if (r.salvo) {
        const a = await ImportarRazaoService.analise(uploadId);
        setAnalise(a);
        const proximo = a.grupos.find((g) => g.status !== "IDENTIFICADO");
        if (proximo) {
          selecionarGrupo(proximo);
          Swal.fire({ icon: "success", text: `Formato ${grupo.id} salvo. Falta o formato ${proximo.id}.`, timer: 2500, showConfirmButton: false });
        } else {
          Swal.fire({ icon: "success", text: "Mapeamento salvo. A importação foi iniciada.", timer: 2500, showConfirmButton: false });
          onConcluido();
        }
      }
    } catch (err: any) {
      Swal.fire({ icon: "error", text: err?.message || "Não foi possível validar o mapeamento." });
    } finally {
      setEnviando(false);
    }
  }

  const linhas = (grupo?.amostra ?? []).slice(0, LINHAS_EXIBIDAS);
  const colDebito = papeis.indexOf("DEBITO");
  const colCredito = papeis.indexOf("CREDITO");
  const precisaConfirmarDC = !!resultado?.ok && resultado.confirmacaoDCObrigatoria;

  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.4)", zIndex: 9999,
        display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(4px)",
      }}
      onClick={onClose}
    >
      <div
        className="card shadow-lg"
        style={{ width: 1180, maxWidth: "96vw", maxHeight: "94vh", borderRadius: 16, border: "none", display: "flex", flexDirection: "column" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="card-header d-flex justify-content-between align-items-center py-4 px-5" style={{ background: "#F8FAFC", borderRadius: "16px 16px 0 0" }}>
          <div>
            <h3 className="card-title fw-bolder text-dark m-0" style={{ fontSize: 18 }}>Ajustar o mapeamento das colunas</h3>
            <div className="text-muted fs-7">
              {empresaNome ? `${empresaNome} · ` : ""}o arquivo não é alterado — indique o que cada coluna representa
            </div>
          </div>
          <button className="btn btn-icon btn-sm btn-active-light-primary" onClick={onClose}>
            <i className="bi bi-x fs-2" />
          </button>
        </div>

        <div className="card-body p-5" style={{ overflowY: "auto" }}>
          {!analise || !grupo ? (
            <div className="text-muted">{analise ? "Nenhum formato pendente de mapeamento." : "Carregando..."}</div>
          ) : (
            <>
              {pendentes.length > 1 && (
                <ul className="nav nav-tabs mb-4">
                  {pendentes.map((g) => (
                    <li className="nav-item" key={g.id}>
                      <button className={`nav-link ${g.id === grupoId ? "active" : ""}`} onClick={() => selecionarGrupo(g)}>
                        Formato {g.id} · {g.arquivoExemplo}
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {grupo.conferenciaMensagem && !resultado && (
                <div className="alert alert-warning py-3" style={{ fontSize: 12 }}>
                  <b>Por que parou aqui:</b> {grupo.conferenciaMensagem}
                  {grupo.divergencias[0] && <div className="mt-1">{grupo.divergencias[0]}</div>}
                </div>
              )}

              <div className="row g-3 mb-4" style={{ fontSize: 12 }}>
                <div className="col-md-4">
                  <label className="form-label fw-bold mb-1">A conta vem</label>
                  <div className="d-flex gap-4">
                    <label className="form-check">
                      <input className="form-check-input" type="radio" checked={base.modoConta === "BLOCO"}
                        onChange={() => mudarBase({ modoConta: "BLOCO" })} />
                      <span className="form-check-label">em bloco (linha própria)</span>
                    </label>
                    <label className="form-check">
                      <input className="form-check-input" type="radio" checked={base.modoConta === "COLUNA"}
                        onChange={() => mudarBase({ modoConta: "COLUNA" })} />
                      <span className="form-check-label">em coluna (cada linha)</span>
                    </label>
                  </div>
                </div>
                <div className="col-md-2">
                  <label className="form-label fw-bold mb-1">Linha do cabeçalho</label>
                  <input type="number" min={0} className="form-control form-control-sm"
                    value={base.linhaCabecalho ?? 0}
                    onChange={(e) => mudarBase({ linhaCabecalho: Number(e.target.value) > 0 ? Number(e.target.value) : null })} />
                  <div className="form-text" style={{ fontSize: 10 }}>0 = arquivo sem cabeçalho</div>
                </div>
                <div className="col-md-3">
                  <label className="form-label fw-bold mb-1">Linha de saldo anterior começa com</label>
                  <input className="form-control form-control-sm" placeholder="ex.: Saldo anterior"
                    value={base.saldoAnteriorTexto ?? ""}
                    onChange={(e) => mudarBase({ saldoAnteriorTexto: e.target.value || null })} />
                </div>
                <div className="col-md-3">
                  <label className="form-label fw-bold mb-1">Linha de total começa com</label>
                  <input className="form-control form-control-sm" placeholder="ex.: Total"
                    value={base.totalTexto ?? ""}
                    onChange={(e) => mudarBase({ totalTexto: e.target.value || null })} />
                </div>
                {base.modoConta === "BLOCO" && (
                  <div className="col-12">
                    <label className="form-label fw-bold mb-1">
                      Reconhecer a linha da conta (regex, opcional — grupos <code>codigo</code> e <code>nome</code>)
                    </label>
                    <input className="form-control form-control-sm font-monospace"
                      placeholder="vazio = linha em que só a coluna Conta está preenchida"
                      value={base.contaCabecalhoRegex ?? ""}
                      onChange={(e) => mudarBase({ contaCabecalhoRegex: e.target.value || null })} />
                  </div>
                )}
              </div>

              <div style={{ overflowX: "auto", border: "1px solid #E4E6EF", borderRadius: 10 }}>
                <table className="table table-sm mb-0" style={{ fontSize: 11, whiteSpace: "nowrap" }}>
                  <thead style={{ position: "sticky", top: 0, background: "#fff" }}>
                    <tr>
                      <th style={{ width: 44 }} />
                      {papeis.map((p, i) => (
                        <th key={i} style={{ minWidth: 130 }}>
                          <div className="text-muted mb-1">{letra(i)}</div>
                          <select className="form-select form-select-sm" style={{ fontSize: 11, fontWeight: p ? 700 : 400 }}
                            value={p} onChange={(e) => mudarPapel(i, e.target.value as Papel)}>
                            {PAPEIS
                              .filter((o) => base.modoConta === "COLUNA" || o.valor !== "CONTA_NOME")
                              .map((o) => <option key={o.valor} value={o.valor}>{o.rotulo}</option>)}
                          </select>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {linhas.map((r) => {
                      const cabecalho = !!base.linhaCabecalho && r.indice + 1 <= base.linhaCabecalho;
                      return (
                        <tr key={r.indice} style={{ background: cabecalho ? "#F1F5F9" : undefined, color: cabecalho ? "#64748B" : undefined }}>
                          <td className="text-muted">L{r.indice + 1}</td>
                          {papeis.map((_, i) => {
                            const v = r.celulas[i] ?? "";
                            return (
                              <td key={i} title={v} style={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis" }}>
                                {v.length > 40 ? `${v.slice(0, 40)}…` : v}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="form-text">Primeiras {linhas.length} linhas de {grupo.arquivoExemplo} (somente leitura).</div>

              {resultado && (
                <div className={`alert ${resultado.ok ? "alert-success" : "alert-danger"} mt-4 py-3`} style={{ fontSize: 12 }}>
                  <b>{resultado.mensagem}</b>
                  {resultado.divergencias.length > 0 && (
                    <ul className="mb-0 mt-2">
                      {resultado.divergencias.map((d, i) => <li key={i}>{d}</li>)}
                    </ul>
                  )}
                  {resultado.ok && resultado.semProvaMatematica && (
                    <div className="mt-1">O arquivo não tem saldo nem total para conferir — confira a prévia abaixo.</div>
                  )}
                </div>
              )}

              {precisaConfirmarDC && (
                <label className="form-check mt-2" style={{ fontSize: 12 }}>
                  <input className="form-check-input" type="checkbox" checked={confirmouDC} onChange={(e) => setConfirmouDC(e.target.checked)} />
                  <span className="form-check-label fw-bold">
                    O arquivo não tem cabeçalho: confirmo que a coluna {colDebito >= 0 ? letra(colDebito) : "?"} é
                    DÉBITO e a coluna {colCredito >= 0 ? letra(colCredito) : "?"} é CRÉDITO.
                  </span>
                </label>
              )}

              {resultado && resultado.previa.length > 0 && (
                <div className="mt-4">
                  <div className="fw-bold mb-2" style={{ fontSize: 12 }}>Como os lançamentos ficam com este mapeamento</div>
                  <table className="table table-sm table-row-dashed" style={{ fontSize: 11 }}>
                    <thead>
                      <tr className="text-muted fw-bold">
                        <th>Linha</th><th>Conta</th><th>Data</th><th>Histórico</th>
                        <th className="text-end">Débito</th><th className="text-end">Crédito</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resultado.previa.map((p) => (
                        <tr key={p.linha}>
                          <td>L{p.linha}</td>
                          <td title={p.nomeConta ?? ""}>{p.conta}</td>
                          <td>{new Date(p.data).toLocaleDateString("pt-BR")}</td>
                          <td className="text-truncate" style={{ maxWidth: 360 }} title={p.historico ?? ""}>{p.historico}</td>
                          <td className="text-end">{p.debito ? brl(p.debito) : ""}</td>
                          <td className="text-end">{p.credito ? brl(p.credito) : ""}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>

        <div className="card-footer d-flex justify-content-end gap-2 py-4 px-5 bg-light-soft" style={{ borderRadius: "0 0 16px 16px" }}>
          <button className="btn btn-sm btn-secondary px-4" onClick={onClose}>Fechar</button>
          <button className="btn btn-sm btn-light-primary px-4" onClick={() => validar(false)} disabled={enviando || !grupo}>
            {enviando ? "Conferindo..." : "Validar"}
          </button>
          <button className="btn btn-sm btn-primary px-4" onClick={() => validar(true)}
            disabled={enviando || !resultado?.ok || (precisaConfirmarDC && !confirmouDC)}>
            Salvar e importar
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModalMapeamentoColunas;
